import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Clock } from "@phosphor-icons/react";
import { API, authHeaders } from "@/lib/api";
import { useLocale } from "@/i18n";
import { formatPrice } from "../booking/format";
import ImportProfile from "./ImportProfile";

export default function ServicesTab({ barber, onImported }) {
  const { t, locale } = useLocale();
  const CATEGORIES = t("categories");
  const EMPTY = { name: "", category: CATEGORIES[0], duration_min: "30", price: "" };
  const [services, setServices] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/barber/services`, { headers: authHeaders() });
      setServices(res.data);
    } catch {
      toast.error(t("svc_load_error"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startEdit = (s) => {
    setEditingId(s.id);
    setForm({ name: s.name, category: s.category, duration_min: String(s.duration_min), price: s.price == null ? "" : String(s.price) });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.warning(t("warn_svc_name"));
    const payload = {
      name: form.name.trim(),
      category: form.category,
      duration_min: Number(form.duration_min) || 30,
      price: form.price === "" ? null : Number(form.price.replace(",", ".")),
    };
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/barber/services/${editingId}`, payload, { headers: authHeaders() });
        toast.success(t("svc_updated"));
      } else {
        await axios.post(`${API}/barber/services`, payload, { headers: authHeaders() });
        toast.success(t("svc_added"));
      }
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch {
      toast.error(t("svc_save_error"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/barber/services/${id}`, { headers: authHeaders() });
      toast.success(t("svc_removed"));
      load();
    } catch {
      toast.error(t("svc_remove_error"));
    }
  };

  const inputCls =
    "w-full h-11 px-3 rounded-lg bg-zinc-950 border border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none transition-colors";

  return (
    <div data-testid="services-tab">
      <form onSubmit={handleSubmit} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3" data-testid="service-form">
        <h2 className="text-sm font-bold text-zinc-300">{editingId ? t("svc_edit") : t("svc_new")}</h2>
        <input
          data-testid="service-name-input"
          type="text"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          placeholder={t("svc_name_ph")}
          className={inputCls}
        />
        <div className="flex gap-3">
          <select
            data-testid="service-category-select"
            value={form.category}
            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
            className={`${inputCls} flex-1`}
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <input
            data-testid="service-duration-input"
            type="number"
            min="5"
            step="5"
            value={form.duration_min}
            onChange={(e) => setForm((f) => ({ ...f, duration_min: e.target.value }))}
            placeholder="Min"
            className={`${inputCls} w-24`}
          />
        </div>
        <input
          data-testid="service-price-input"
          type="text"
          inputMode="decimal"
          value={form.price}
          onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
          placeholder={t("svc_price_ph")}
          className={inputCls}
        />
        <div className="flex gap-2">
          <button
            data-testid="service-save-btn"
            type="submit"
            disabled={saving}
            className="flex-1 h-11 rounded-lg bg-amber-500 text-zinc-950 font-bold flex items-center justify-center gap-2 hover:bg-amber-400 transition-colors active:scale-95 disabled:opacity-60"
          >
            <Plus size={16} weight="bold" />
            {saving ? t("svc_saving") : editingId ? t("svc_save") : t("svc_add")}
          </button>
          {editingId && (
            <button
              data-testid="service-cancel-edit-btn"
              type="button"
              onClick={() => { setEditingId(null); setForm(EMPTY); }}
              className="h-11 px-4 rounded-lg border border-zinc-800 text-zinc-400 text-sm font-semibold hover:border-zinc-600 transition-colors"
            >
              {t("cancel")}
            </button>
          )}
        </div>
      </form>

      <div className="mt-5 space-y-2" data-testid="services-list">
        {!services ? (
          <div className="flex justify-center py-6">
            <div className="h-8 w-8 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" />
          </div>
        ) : services.length === 0 ? (
          <p className="text-sm text-zinc-500 bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="no-services-msg">
            {t("svc_empty")}
          </p>
        ) : (
          services.map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-3 bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3" data-testid={`service-row-${s.id}`}>
              <div className="min-w-0">
                <p className="font-semibold text-zinc-100 truncate">{s.name}</p>
                <p className="text-xs text-zinc-500 flex items-center gap-1 mt-0.5">
                  <span className="text-amber-500/80">{s.category}</span> ·
                  <Clock size={12} weight="duotone" /> {s.duration_min} min · {formatPrice(s.price, locale)}
                </p>
              </div>
              <div className="flex gap-1 shrink-0">
                <button
                  data-testid={`service-edit-btn-${s.id}`}
                  onClick={() => startEdit(s)}
                  className="p-2 text-zinc-400 hover:text-amber-500 transition-colors"
                >
                  <PencilSimple size={17} weight="bold" />
                </button>
                <button
                  data-testid={`service-delete-btn-${s.id}`}
                  onClick={() => remove(s.id)}
                  className="p-2 text-zinc-500 hover:text-red-400 transition-colors"
                >
                  <Trash size={17} weight="bold" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {!barber?.imported && (
        <div className="mt-6">
          <ImportProfile onImported={() => { load(); onImported?.(); }} />
        </div>
      )}
    </div>
  );
}
